/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { GState, jsPDF, jsPDFOptions, TextOptionsLight } from 'jspdf'
import { Table } from 'jspdf-autotable'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { PrintHelper } from '@/Helpers/Utils/PrintHelper'

/**************************************************************************************************/
/** @internal Type @name Height                                                                  **/
/**************************************************************************************************/
type Height = number

/**************************************************************************************************/
/** @internal Type @name TextOptions                                                             **/
/**************************************************************************************************/
type TextOptions = TextOptionsLight & { [key: string]: string }

/**************************************************************************************************/
/** @internal Type @name XPos                                                                    **/
/**************************************************************************************************/
type XPos = number

/**************************************************************************************************/
/** @internal Type @name YPos                                                                    **/
/**************************************************************************************************/
type YPos = number

/**************************************************************************************************/
/** @internal Type @name Width                                                                   **/
/**************************************************************************************************/
type Width = number

/**************************************************************************************************/
/** @export Class @name PDF                                                                      **/
/**************************************************************************************************/
export class LegacyPDF
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	private _availableHeightSpace = 0
	private _currentPage = 1
	private _defaultHeaderHeight = 24
	private _defaultFooterHeight = 24
	private _defaultFontFamily = 'OpenSans-Normal'
	private _doc!: jsPDF
	private _globalX = 0
	private _globalY = 0

	private _docOptions: jsPDFOptions =
	{
		format: 'letter',
		orientation: 'p',
		unit: 'pt'
	}

	private _miscOptions: PDFMiscOptions =
	{
		drawFooterSafeArea: false,
		drawHeaderSafeArea: false,
		margin: 28
	}

	private _textOptions: TextOptions =
	{
		align: 'left',
		baseline: 'top'
	}

	/*************************************************/
	/** @Constructor                                **/
	/*************************************************/
	constructor(options?: jsPDFOptions, miscOptions?: PDFMiscOptions)
	{
		const _options     = options || {}
		this._docOptions   = { ...this._docOptions, ..._options }
		const _miscOptions = miscOptions || {}
		this._miscOptions  = { ...this._miscOptions, ..._miscOptions }
	}

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	public addImage(imagePath: string, ...args: [XPos, YPos, Width, Height, boolean?]) : this
	{
		// Propiedades Validas
		let w, h

		// Asignar los valores a las propiedades.
		switch (args.length)
		{
			case 5:
			case 4:
				this.updateGlobalXY(args[0], args[1])
				w = args[2]
				h = args[3]
				break
		}

		this._doc.addImage(new Image().src = imagePath, 'PNG', this.getGlobalX(), this.getGlobalY(), w, h)

		// Verificar si el ultimo argumento es booleano.
		const lastArg = args[args.length - 1]
		if (typeof lastArg === 'boolean' && lastArg === true)
		{
			this.updateGlobalY(h)
		}

		return this
	}

	public addImageCentered(imagePath: string, ...args: [YPos, Width, Height, boolean?]) : this
	{
		// Propiedades Validas
		let w, h

		// Asignar los valores a las propiedades.
		switch (args.length)
		{
			case 4:
			case 3:
				this.updateGlobalXY(0, args[0])
				w = args[1]
				h = args[2]
				break
		}

		const centeredX = (this.getPageWidth() - w) / 2
		this._doc.addImage(new Image().src = imagePath, 'PNG', centeredX, this.getGlobalY(), w, h)

		// Verificar si el ultimo argumento es booleano.
		const lastArg = args[args.length - 1]
		if (typeof lastArg === 'boolean' && lastArg === true)
		{
			this.updateGlobalY(h)
		}

		return this
	}

	public addNumberOfPages() : void
	{
		this.setFontColor('#555')
		this.setFontSize(8)
		this.updateTextOption('align', 'right')

		for (let pageNumber = 1; pageNumber <= this.getNumPages(); pageNumber++)
		{
			this._doc.setPage(pageNumber)
			this._doc.text(`Página ${ pageNumber } de ${ this.getNumPages() }`, this.getPageWidth() + this._miscOptions.margin!, this._miscOptions.margin!, this._textOptions)
		}
	}

	public addText(text: string, x = 0, y = 0, size = 0, includeTextHeight = false) : this
	{
		const textLines = this._doc.splitTextToSize(text, this.getPageWidth())
		const textHeight = includeTextHeight ? this.getTextHeight(textLines) + this._doc.getLineHeightFactor() : 0
		this.updateAvailableHeightSpace(this.getTextHeight(textLines))

		if (size > 0) this.setFontSize(size)
		this.updateGlobalXY(x, y)
		this._doc.text(textLines, this.getGlobalX(), this.getGlobalY(), this._textOptions)

		this.updateGlobalY(textHeight)
		return this
	}

	public addTextCentered(text: string, y = 0, size = 0, includeTextHeight = false) : this
	{
		const textLines = this._doc.splitTextToSize(text, this.getPageWidth())
		const textHeight = includeTextHeight ? this.getTextHeight(textLines) + this._doc.getLineHeightFactor() : 0
		this.updateAvailableHeightSpace(this.getTextHeight(textLines))

		if (size > 0) this.setFontSize(size)
		this.updateGlobalXY(0, y)
		this._doc.text(text, this.getCenteredPagePosForText(text), this.getGlobalY(), this._textOptions)

		this.updateGlobalY(textHeight)
		return this
	}

	public addTextRight(text: string, x = 0, y = 0, size = 0, includeTextHeight = false) : this
	{
		const textLines = this._doc.splitTextToSize(text, this.getPageWidth())
		const textHeight = includeTextHeight ? this.getTextHeight(textLines) + this._doc.getLineHeightFactor() : 0
		this.updateAvailableHeightSpace(this.getTextHeight(textLines))

		this.updateTextOption('align', 'right')
		if (size > 0) this.setFontSize(size)
		this.updateGlobalXY(x, y)
		this._doc.text(textLines, this.getPageWidth() + this.getPageMargin(), this.getGlobalY(), this._textOptions)
		this.updateTextOption('align', 'left')

		this.updateGlobalY(textHeight)
		return this
	}

	public create(headerText: string | null = null, logoPath: string | null = null, drawFirstPageHeader = false, drawFirstPageFooter = false) : this
	{
		this._doc = new jsPDF(this._docOptions)
		this._doc.setPage(1)

		this.setFont(this._defaultFontFamily)
		this._availableHeightSpace = this.getPageHeight()

		return this
	}

	public forEveryPage(startFrom: number, callback: (self: LegacyPDF, pageIndex: number, isLastPage: boolean) => void) : void
	{
		const totalPages  = this.getNumPages()
		for (let i = startFrom; i <= totalPages; i++)
		{
			this._doc.setPage(i)
			callback(this, i, i === totalPages)
		}
	}

	public getAvailableHeightSpace() : number
	{
		return this._availableHeightSpace
	}

	public getCenteredPagePosForText(text: string) : number
	{
		const pageWidth = this.getPageWidth(false)
		const txtWidth  = this.getTextWidth(text)
		return ( pageWidth - txtWidth ) / 2
	}

	public getCurrentPage() : number
	{
		return this._currentPage
	}

	public getDefaultFooterHeight() : number
	{
		return this._defaultFooterHeight
	}

	public getDefaultHeaderHeight() : number
	{
		return this._defaultHeaderHeight
	}

	public getFontHeight() : number
	{
		return this._doc.getFontSize()
	}

	public getGlobalDoc() : jsPDF
	{
		return this._doc
	}

	public getGlobalX() : number
	{
		return this._globalX + this.getPageMargin()
	}

	public getGlobalY() : number
	{
		return this._globalY + this.getPageMargin()
	}

	public getLastTable() : Table
	{
		const doc: any = this._doc
		return doc.lastAutoTable as Table
	}

	public getNumPages() : number
	{
		return (<any> this._doc.internal).getNumberOfPages()
	}

	public getLastTableY() : number
	{
		const lastTable = this.getLastTable()
		return lastTable.body[lastTable.body.length - 1].cells[0].y - 6
	}

	public getPageHeight(includePageMargin = true) : number
	{
		const pageHeight = this._doc.internal.pageSize.height
		const marginOffset = this._miscOptions.margin!
		return pageHeight - (includePageMargin ? marginOffset * 2 : 0)
	}

	public getPageMargin() : number
	{
		return this._miscOptions.margin!
	}

	public getPageWidth(includePageMargin = true) : number
	{
		const pageWidth = this._doc.internal.pageSize.width
		const marginOffset = this._miscOptions.margin!
		return pageWidth - (includePageMargin ? marginOffset * 2 : 0)
	}

	private GetScaleFactor() : number
	{
		return this._doc!.internal.scaleFactor
	}

	public getTextHeight(text: string | Array<string>) : number
	{
		return this.getFontHeight() * (Array.isArray(text) ? text.length : 1)
	}

	public getTextOptions() : TextOptions
	{
		return this._textOptions
	}

	public getTextWidth(text: string | Array<string>) : number
	{
		if (Array.isArray(text))
		{
			let maxWidth = 0
			for (const line of text)
			{
				const w = this._doc.getStringUnitWidth(line)
				if (w > maxWidth) maxWidth = w
			}
			return maxWidth * this._doc.getFontSize() / this.GetScaleFactor()
		}
		else
		{
			return this._doc.getStringUnitWidth(text) * this._doc.getFontSize() / this.GetScaleFactor()
		}
	}

	public open(fileName = 'generated') : void
	{
		this._doc.setProperties({ title: fileName })
		window.open(<any> this._doc.output('bloburl'))
	}

	public save(fileName: string) : this
	{
		this._doc.save(`${ fileName }.pdf`)
		return this
	}

	public setAvailableHeightSpace(h: number) : this
	{
		this._availableHeightSpace = h
		return this
	}

	public setCurrentPage(p: number) : this
	{
		this._currentPage = p
		return this
	}

	public setFillColor(hex: string) : this
	{
		this._doc.setFillColor(hex)
		return this
	}

	public setFont(name: string, style?: string, weight?: number | string) : this
	{
		this._doc.setFont(name, style, weight)
		return this
	}

	public setFontColor(hex: string) : this
	{
		this._doc.setTextColor(hex)
		return this
	}

	public setFontSize(size: number) : this
	{
		this._doc.setFontSize(size)
		return this
	}

	public setGlobalY(y: number) : this
	{
		this._globalY = y
		return this
	}

	public setStrokeColor(hex: string) : this
	{
		this._doc.setDrawColor(hex)
		return this
	}

	public setTempGStateProps(props: GState, callback: (self: LegacyPDF) => void) : void
	{
		this._doc.saveGraphicsState()
		this._doc.setGState(new GState(props))
		callback(this)
		this._doc.restoreGraphicsState()
	}

	public setTextOptions(options: TextOptions) : this
	{
		this._textOptions = options
		return this
	}

	public updateAvailableHeightSpace(v: number, preventNewPageAdded = false) : boolean
	{
		const newPageHeight = this._availableHeightSpace - v
		this._availableHeightSpace = newPageHeight
		PrintHelper.warning('[PDF]:updateAvailableHeightSpace() AvailableHeightSpace:', this._availableHeightSpace)

		if (this._availableHeightSpace < 0 && (!preventNewPageAdded))
		{
			PrintHelper.warning('[PDF]:updateAvailableHeightSpace() New Page Added!')
			this.setGlobalY(16)
			this._availableHeightSpace = this.getPageHeight()
			this._doc.addPage()
			return true
		}
		return false
	}

	public ensureSpace(heightNeeded: number) : LegacyPDF
	{
		const remaining = this.getAvailableHeightSpace()
		if (remaining < heightNeeded)
		{
			this._doc.addPage()
			this.setGlobalY(0)
			this.setAvailableHeightSpace(this.getPageHeight())
		}
		return this
	}

	public updateGlobalX(x: number) : void
	{
		this._globalX = x
	}

	public updateGlobalY(y: number) : void
	{
		this._globalY += y
	}

	public updateGlobalXY(x: number, y: number) : this
	{
		this.updateGlobalX(x)
		this.updateGlobalY(y)
		return this
	}

	public updateTextOption(option: string, value: any) : this
	{
		this._textOptions[option] = value
		return this
	}
}

/**************************************************************************************************/
/** @export Interface @name PDFMiscOptions                                                       **/
/**************************************************************************************************/
export interface PDFMiscOptions
{
	drawFooterSafeArea?: boolean
	drawHeaderSafeArea?: boolean
	margin?: number
}